import {
  addSeconds,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  getDay,
  isValid,
  startOfDay,
} from "date-fns";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";

import {
  FIRST_BREAK_DELAY_HOURS,
  FIRST_BREAK_DURATION_HOURS,
  SECONDS_PER_DAY,
  SECONDS_PER_HOUR,
  SECOND_BREAK_DELAY_HOURS,
  WEEKEND_DAYS,
} from "./timesheetHelper";

import { DayOfWeek } from "@/app/app.interface";
import { APP_LOCAL_KEYS, ONE, States } from "@/app/constants/constants";
import { CustomerVerified } from "@/features/customerPortal";
import { dayNamesUpper } from "@/features/projectDailyReport/projectDailyReport.constants";
import { TIMESHEET_WORKING_HOURS } from "@/features/timeTrackingPayroll/timeTrackingPayroll.constants";
import {
  BreakDown,
  Holiday,
  TimeShift,
} from "@/features/timeTrackingPayroll/timeTrackingPayroll.interfaces";
import { isOvernightTime } from "@/features/timeTrackingPayroll/utils/timesheet.helper";
import { Workspace } from "@/features/workspace/workspace.interfaces";
import { selectWorkspaceTimeZone } from "@/redux/slices/userSlice";
import { store } from "@/redux/store";
import { isCustomerDomain } from "@/utils/core/generalUtils";
import { APP_CONFIG } from "@/utils/storage/appConfig";
import { typedLocalStorage } from "@/utils/storage/localStorage";

const MAX_SAFE_DATE_RANGE_DAYS = 370;

type GetMainBreakDownProps = {
  startTime: number;
  endTime: number;
  shifts: TimeShift[];
  day: DayOfWeek | string;
  holiday?: Holiday;
  originalDay?: DayOfWeek | string; // Add original day context for overnight shifts
  breakHours?: number; // Break time in hours
};

type GetMainBreakDownReturn = {
  id: string;
  time: number;
  day: string;
  title: string;
  start: number;
  end: number;
};

type DateRangeCheckParams = {
  startDate?: string | number | Date | null;
  endDate?: string | number | Date | null;
  date?: string | number | Date;
};

type TimelineBreakDeductionParams = {
  breakDowns: GetMainBreakDownReturn[];
  startTime: number;
  endTime: number;
  breakHours: number;
  day: DayOfWeek | string;
  holiday?: Holiday;
};

/**
 * Apply break deduction to breakdowns based on timing and spillover logic
 * @param {Array} breakDowns - Array of breakdown objects
 * @param {number} breakTime - Time when break occurs (in seconds)
 * @param {number} breakSeconds - Break duration in seconds
 * @param {DayOfWeek | string} day - Current day
 * @returns {void} Does not return a value.
 */
function applyBreakToBreakdowns(
  breakDowns: {
    id: string;
    time: number;
    title: string;
    day: string;
    start: number;
    end: number;
  }[],
  breakTime: number,
  breakSeconds: number,
  day: DayOfWeek | string,
): void {
  // Convert day string to day number for weekend check
  // Safeguard against undefined/null day parameter
  const dayString = day ? day.toString().toUpperCase() : "MONDAY";
  const dayIndex = dayNamesUpper.indexOf(dayString);
  const isWeekend = WEEKEND_DAYS.includes(dayIndex);

  // Determine which category this break falls into based on time and day
  let targetBreakdownType: string;

  if (isWeekend) {
    targetBreakdownType = "Weekend";
  } else if (isOvernightTime(breakTime)) {
    targetBreakdownType = "Overnight Hours";
  } else {
    targetBreakdownType = "Normal Hours";
  }

  // Find the target breakdown
  const targetBreakdown = breakDowns.find(
    (b) => b.title === targetBreakdownType,
  );

  if (targetBreakdown && targetBreakdown.time >= breakSeconds) {
    // Sufficient time in target breakdown
    targetBreakdown.time -= breakSeconds;
  } else {
    // Handle spillover
    let remainingBreakSeconds = breakSeconds;

    if (targetBreakdown && targetBreakdown.time > 0) {
      remainingBreakSeconds -= targetBreakdown.time;
      targetBreakdown.time = 0;
    }

    // Apply spillover to other breakdowns
    // Priority: Normal Hours -> Overnight Hours -> Weekend
    const spilloverPriority = ["Normal Hours", "Overnight Hours", "Weekend"];

    for (const breakdownType of spilloverPriority) {
      if (remainingBreakSeconds <= 0) {
        break;
      }
      if (breakdownType === targetBreakdownType) {
        continue;
      } // Skip already processed

      const spilloverBreakdown = breakDowns.find(
        (b) => b.title === breakdownType,
      );
      if (spilloverBreakdown && spilloverBreakdown.time > 0) {
        const deductAmount = Math.min(
          spilloverBreakdown.time,
          remainingBreakSeconds,
        );
        spilloverBreakdown.time -= deductAmount;
        remainingBreakSeconds -= deductAmount;
      }
    }
  }

  // Ensure no negative times
  breakDowns.forEach((breakdown) => {
    if (breakdown.time < 0) {
      breakdown.time = 0;
    }
  });
}

/**
 * @description it converts seconds to date
 * @param {number} sec - seconds
 * @returns {Date | string} - date
 */
export const secToDate = (sec: number): Date => {
  const date = new Date();
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = Math.floor(sec % 60);
  date.setHours(hours, minutes, seconds, 0);

  return date;
};

/**
 * @description it converts seconds to date in 1H:30M format
 * @param {number} sec - seconds
 * @returns {string} - date in 1H:30M format
 */
export const secToDateFormat = (sec: number): string => {
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);

  return `${hours} H : ${minutes} M`;
};

/**
 * @description convert the seconed to 30mins or if above 1 hour the 1hr 30 mins
 * @param sec {number} -seconed
 * @returns {string} return time format
 */

export const secToTimeString = (sec: number): string => {
  if (sec === 0) {
    return "0 min";
  }
  if (sec < 60) {
    return `${sec} sec${sec !== 1 ? "s" : ""}`;
  }
  if (sec < 3600) {
    return `${Math.floor(sec / 60)} min${sec / 60 !== 1 ? "s" : ""}`;
  }

  return `${Math.floor(sec / 3600)} hr${Math.floor((sec % 3600) / 60) !== 1 ? "s" : ""} ${Math.floor((sec % 3600) / 60)} min${Math.floor((sec % 3600) / 60) !== 1 ? "s" : ""}`;
};

/**
 * @description convert the seconed to AMPM
 * @param sec {number} -seconed
 * @returns {string} return time format
 */

export const seconedToHourMin = (sec: number): string => {
  if (sec === 0) {
    return "12:00 AM";
  }
  if (!sec) {
    return "0 H : 0 M";
  }
  const startofDay = startOfDay(new Date());
  const timeofDay = addSeconds(startofDay, sec);
  const ampm = format(timeofDay, "hh:mm a");

  return `${ampm}`;
};

/**
 * @description convert the second to 0.5 hrs if it is 30 mins and 1.25 hrs if it is 1 hr 15 mins very generic example
 * @param {number} sec - seconds to convert to float hours
 * @returns {number} time in float hours format (e.g. 0.5 for 30 mins, 1.25 for 1 hr 15 mins)
 */
export const secToFloatHours = (sec: number): number => {
  if (sec === 0) {
    return 0;
  }
  if (sec < 3600) {
    return sec / 3600;
  }

  // For hours and above, convert to float hours
  return Number((sec / 3600).toFixed(2));
};

/**
 * @description Converts a date to seconds since the start of the day.
 * @param {Date} date - The date to convert.
 * @returns {number} - The number of seconds since the start of the day.
 */
export const dateToSec = (date: Date | undefined): number => {
  const hours = date?.getHours() ?? 0;
  const minutes = date?.getMinutes() ?? 0;
  const secs = date?.getSeconds() ?? 0;

  return hours * 3600 + minutes * 60 + secs;
};

// donot change this order
export const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * @description Orders flat shift rows: Normal → Overnight → Weekend. Unknown titles last.
 * @param {TimeShift[]} shifts - list of shifts
 * @returns {TimeShift[]} - sorted copy
 */
export function sortTimeShiftsByTitleOrder(shifts: TimeShift[]): TimeShift[] {
  const order: string[] = [
    TIMESHEET_WORKING_HOURS.NORMAL_HOURS,
    TIMESHEET_WORKING_HOURS.OVERNIGHT_HOURS,
    TIMESHEET_WORKING_HOURS.WEEKEND,
  ];
  const rank = (title: string | undefined): number => {
    const i = order.indexOf(title ?? "");

    return i === -1 ? order.length : i;
  };

  return [...shifts].sort((a, b) => rank(a.title) - rank(b.title));
}

/**
 * @description it sorts the shifts
 * @param {TimeShift[]} shifts - list of shifts
 * @returns {TimeShift[]} - sorted shifts
 */
export function sortShifts(shifts: TimeShift[]): TimeShift[][] {
  return Array(shifts)?.sort((a: TimeShift[], b: TimeShift[]) => {
    const titles = ["Normal Hours", "Overnight Hours", "Weekend"];
    const indexA = titles.indexOf(a[0].title!);
    const indexB = titles.indexOf(b[0].title!);
    if (indexA < indexB) {
      return -1;
    }
    if (indexA > indexB) {
      return 1;
    }

    return 0;
  });
}

/**
 * @description it gives the next day
 * @param {string} currentDay - current day
 * @returns {string} - next day
 */
function nextDay(currentDay: string): string {
  // Find the index of the current day
  const currentIndex = dayNames.indexOf(currentDay);

  // Calculate the index of the next day
  const nextIndex = (currentIndex + 1) % 7;

  // Return the name of the next day
  return dayNames[nextIndex];
}

/**
 * Merges breakdown rows that share the same title (sums time, widens start/end hour range).
 * @param {GetMainBreakDownReturn[]} entries - Breakdown rows from split shift segments
 * @returns {GetMainBreakDownReturn[]} One row per distinct title
 */
function mergeBreakdownsByTitle(
  entries: GetMainBreakDownReturn[],
): GetMainBreakDownReturn[] {
  const byTitle = new Map<string, GetMainBreakDownReturn>();

  for (const row of entries) {
    const existing = byTitle.get(row.title);
    if (!existing) {
      byTitle.set(row.title, { ...row });
    } else {
      existing.time += row.time;
      existing.start = Math.min(existing.start, row.start);
      existing.end = Math.max(existing.end, row.end);
    }
  }

  return [...byTitle.values()];
}

/**
 * Applies weekday first/second break deductions using a single shift timeline (`startTime`/`endTime`).
 * Mutates `breakDowns` in place.
 * @param {TimelineBreakDeductionParams} props - Timeline break deduction parameters
 * @returns {void} Does not return a value.
 */
function applyWeekdayTimelineBreakDeduction(
  props: TimelineBreakDeductionParams,
): void {
  const { breakDowns, startTime, endTime, breakHours, day } = props;
  const dayIndex = dayNamesUpper.indexOf(day.toString().toUpperCase());
  const isWeekend = WEEKEND_DAYS.includes(dayIndex);

  if (breakHours <= 0 || breakDowns.length === 0 || isWeekend) {
    return;
  }

  const shiftDurationSeconds =
    startTime < endTime
      ? endTime - startTime
      : SECONDS_PER_DAY - startTime + endTime;

  const firstBreakTime = startTime + FIRST_BREAK_DELAY_HOURS * SECONDS_PER_HOUR;
  const secondBreakTime =
    startTime + SECOND_BREAK_DELAY_HOURS * SECONDS_PER_HOUR;

  const firstBreakAmount = Math.min(FIRST_BREAK_DURATION_HOURS, breakHours);
  const firstBreakSeconds = firstBreakAmount * SECONDS_PER_HOUR;

  applyBreakToBreakdowns(breakDowns, firstBreakTime, firstBreakSeconds, day);

  const remainingBreakHours = breakHours - firstBreakAmount;
  if (remainingBreakHours <= 0) {
    return;
  }

  const remainingBreakSeconds = remainingBreakHours * SECONDS_PER_HOUR;

  let secondBreakDeductionTime: number;
  if (shiftDurationSeconds < SECOND_BREAK_DELAY_HOURS * SECONDS_PER_HOUR) {
    secondBreakDeductionTime = endTime;
  } else {
    secondBreakDeductionTime = secondBreakTime;
  }

  applyBreakToBreakdowns(
    breakDowns,
    secondBreakDeductionTime,
    remainingBreakSeconds,
    day,
  );
}

/**
 * After merging split overnight / full-day segments, applies break rules for weekend (simple) or weekday (timeline).
 * Mutates merged rows in place.
 * @param {TimelineBreakDeductionParams} params - Merged breakdown and shift timeline
 * @returns {void} Does not return a value.
 */
function applyMergedSplitBreakDeduction(
  params: TimelineBreakDeductionParams,
): void {
  const {
    breakDowns: merged,
    startTime,
    endTime,
    breakHours,
    day,
    holiday,
  } = params;
  if (breakHours <= 0 || merged.length === 0) {
    return;
  }

  const upperDay = day.toString().toUpperCase();
  const dayIndex = dayNamesUpper.indexOf(upperDay);
  const isWeekend = WEEKEND_DAYS.includes(dayIndex);
  const isHoliday =
    holiday && dayNames[getDay(holiday.date)].toUpperCase() === upperDay;

  if (isWeekend || isHoliday) {
    const breakSeconds = breakHours * SECONDS_PER_HOUR;
    const targetTitles = isWeekend
      ? ["Weekend"]
      : ["Overnight Hours", "Normal Hours"];

    for (const row of merged) {
      if (targetTitles.includes(row.title)) {
        row.time = Math.max(0, row.time - breakSeconds);
      }
    }

    return;
  }

  applyWeekdayTimelineBreakDeduction({
    breakDowns: merged,
    startTime,
    endTime,
    breakHours,
    day,
  });
}

/**
 * @description it gives the starttime endtime and time that is spent on each shift
 * @param {GetMainBreakDownProps} object - get main breakdown props
 * @returns {GetMainBreakDownReturn[]} - list of breakdowns
 */
export function getBreakDown({
  startTime,
  endTime,
  shifts,
  day,
  holiday,
  originalDay,
  breakHours = 0,
}: GetMainBreakDownProps): GetMainBreakDownReturn[] {
  // Safeguard against undefined parameters
  if (!day || !shifts || shifts.length === 0) {
    return [];
  }

  let refinedShifts = [];

  for (let i = 0; i < shifts?.length; i++) {
    const s = shifts[i];

    if (s.startTime <= s.endTime) {
      refinedShifts.push(s);
    } else {
      // if startTime is greater than the end time
      // then it means that the shift extends to the next day
      // so we split it into two shifts then merge the breakdowns
      refinedShifts.push({ ...s, startTime: s.startTime, endTime: 86400 });
      refinedShifts.push({ ...s, startTime: 0, endTime: s.endTime });
    }
  }

  refinedShifts = refinedShifts.sort((a, b) =>
    a.rateConversion < b.rateConversion ? 1 : -1,
  );

  const breakDowns: {
    id: string;
    time: number;
    day: string;
    title: string;
    start: number;
    end: number;
  }[] = [];

  const isHoliday =
    holiday &&
    dayNames[getDay(holiday.date)].toUpperCase() === day.toUpperCase();

  const overnightShift = refinedShifts.find(
    (shift) => shift.title === "Overnight Hours",
  )!;

  // if it is holiday then just return the shift as it is
  if (isHoliday && startTime <= endTime && endTime <= 86400) {
    // Handle case where startTime equals endTime (full day holiday)
    let timeSpent = startTime === endTime ? 86400 : endTime - startTime;

    // Apply break deduction for holidays (weekend logic)
    if (breakHours > 0) {
      const breakSeconds = breakHours * SECONDS_PER_HOUR;
      timeSpent = Math.max(0, timeSpent - breakSeconds);
    }

    breakDowns.push({
      id: overnightShift.id,
      start: startTime / 3600,
      end: startTime === endTime ? 24 : endTime / 3600,
      time: timeSpent,
      title: "Overnight Hours",
      day,
    });

    return breakDowns;
  }

  const dayIndex = dayNamesUpper.indexOf(day.toString().toUpperCase());
  const isWeekend = WEEKEND_DAYS.includes(dayIndex);

  for (let i = 0; i < refinedShifts.length; i++) {
    const currentInterval = refinedShifts[i];

    // Use originalDay for shift matching if available (for overnight shifts)
    // This ensures that Friday->Saturday uses Friday's shift config, Sunday->Monday uses Sunday's shift config
    const dayForShiftMatching = originalDay || day;

    // if given start and end time falls inside the refined shifts
    if (
      startTime < currentInterval.endTime &&
      endTime > currentInterval.startTime &&
      currentInterval?.days.includes(dayForShiftMatching?.toUpperCase())
    ) {
      const startTimeInInterval = Math.max(
        currentInterval.startTime,
        startTime,
      );
      const endTimeInInterval = Math.min(currentInterval.endTime, endTime);
      let timeInInterval = endTimeInInterval - startTimeInInterval;

      // For weekend shifts, apply simple break deduction
      if (isWeekend && currentInterval.title === "Weekend" && breakHours > 0) {
        const breakSeconds = breakHours * SECONDS_PER_HOUR;
        timeInInterval = Math.max(0, timeInInterval - breakSeconds);
      }

      breakDowns.push({
        id: currentInterval.id,
        start: startTimeInInterval / 3600,
        end: endTimeInInterval / 3600,
        time: timeInInterval,
        title: currentInterval.title || "",
        day,
      });
    }
  }

  applyWeekdayTimelineBreakDeduction({
    breakDowns,
    startTime,
    endTime,
    breakHours,
    day,
  });

  return breakDowns;
}

/**
 * @description it gives the starttime endtime and time that is spent on each shift
 * @param {GetMainBreakDownProps} object - get main breakdown props
 * @returns {GetMainBreakDownReturn[]} - list of breakdowns
 */
export function getMainBreakDown({
  startTime,
  endTime,
  shifts,
  day,
  holiday,
  originalDay,
  breakHours = 0,
}: GetMainBreakDownProps): GetMainBreakDownReturn[] {
  // Safeguard against undefined parameters
  if (!day || !shifts || shifts.length === 0) {
    return [];
  }

  let result = [];

  // Handle case where startTime equals endTime (represents a full 24-hour period)
  if (startTime === endTime) {
    const breakDown1 = getBreakDown({
      startTime,
      endTime: 86400,
      day,
      shifts,
      holiday,
      originalDay: originalDay || day,
      breakHours: 0,
    });

    const breakDown2 = getBreakDown({
      startTime: 0,
      endTime,
      shifts,
      day: nextDay(day),
      holiday,
      originalDay: originalDay || day,
      breakHours: 0,
    });

    const merged = mergeBreakdownsByTitle([...breakDown1, ...breakDown2]);
    applyMergedSplitBreakDeduction({
      breakDowns: merged,
      startTime,
      endTime,
      breakHours,
      day,
      holiday,
    });
    result = merged;
  } else if (startTime < endTime) {
    result = getBreakDown({
      shifts,
      startTime,
      endTime,
      day,
      holiday,
      originalDay: originalDay || day,
      breakHours,
    });
  } else {
    const breakDown1 = getBreakDown({
      startTime,
      endTime: 86400,
      day,
      shifts,
      holiday,
      originalDay: originalDay || day,
      breakHours: 0,
    });

    const breakDown2 = getBreakDown({
      startTime: 0,
      endTime,
      shifts,
      day: nextDay(day),
      holiday,
      originalDay: originalDay || day,
      breakHours: 0,
    });

    const merged = mergeBreakdownsByTitle([...breakDown1, ...breakDown2]);
    applyMergedSplitBreakDeduction({
      breakDowns: merged,
      startTime,
      endTime,
      breakHours,
      day,
      holiday,
    });
    result = merged;
  }

  return result;
}

/**
 * @description it gives the total hours between start and end time
 * @param {Date} startTime - start time
 * @param {Date} endTime - end time
 * @param {number} breakTime - break time
 * @param {number} travelTime - travel time
 * @returns {number} - total hours
 */
export const calculateTotalUserHours = (
  startTime: Date | undefined,
  endTime: Date | undefined,
  breakTime: number = 0,
  travelTime: number = 0,
): number => {
  if (!startTime || !endTime) {
    return 0;
  }

  const startSeconds = dateToSec(startTime);
  const endSeconds = dateToSec(endTime);
  const isSameDay = startTime.toDateString() === endTime.toDateString();

  let totalSeconds: number;

  if (isSameDay) {
    if (startSeconds < endSeconds) {
      totalSeconds = endSeconds - startSeconds;
    } else if (startSeconds === endSeconds) {
      totalSeconds = 86400;
    } else {
      totalSeconds = 86400 - startSeconds + endSeconds;
    }
  } else {
    totalSeconds = 86400 - startSeconds + endSeconds;
  }

  const adjustedSeconds = totalSeconds - breakTime + travelTime;

  return adjustedSeconds / 3600;
};

export const toUTC = (date: Date | string): string | Date => {
  if (typeof date === "string") {
    return date;
  }

  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ),
  );
};

/**
 * @description Converts a local holiday date to an ISO datetime string for API payloads.
 * @param {Date | string} date - Holiday date value.
 * @returns {string} - Normalized ISO datetime.
 */
export const formatHolidayDateForApi = (date: Date | string): string => {
  const utcDate = toUTC(date);
  const normalizedDate = utcDate instanceof Date ? utcDate : new Date(utcDate);

  return normalizedDate.toISOString();
};

/**
 * @description it calculates the total working hours
 * @param {Date} startTime - start time
 * @param {Date} endTime - end time
 * @param {number} breakTime - break time (in seconds)
 * @param {number} travelTime - travel time (in seconds)
 * @returns {number} - total working hours
 */
export const calculateTotalWorkingHours = (
  startTime: Date,
  endTime: Date,
  breakTime: number = 0,
  travelTime: number = 0,
): number => {
  const start = dateToSec(startTime);
  const end = dateToSec(endTime);

  let totalSeconds: number;

  if (start < end) {
    totalSeconds = end - start;
  } else if (start === end) {
    totalSeconds = 86400;
  } else {
    totalSeconds = 86400 - start + end;
  }

  return (totalSeconds - breakTime + travelTime) / 3600;
};

/**
 * @param {Date} startDate - The start date
 * @param {Date} endDate - The end date
 * @returns {Date[]} - The range of dates between the start and end date
 * @description it takes start and end date and return range of dates between them
 */
export function getDateRange(
  startDate: Date | string,
  endDate: Date | string,
): Date[] {
  const parsedStartDate = new Date(startDate);
  const parsedEndDate = new Date(endDate);
  const rangeDays = differenceInCalendarDays(parsedEndDate, parsedStartDate);

  if (
    !isValid(parsedStartDate) ||
    !isValid(parsedEndDate) ||
    rangeDays < 0 ||
    rangeDays > MAX_SAFE_DATE_RANGE_DAYS
  ) {
    return [];
  }

  return eachDayOfInterval({
    start: parsedStartDate,
    end: parsedEndDate,
  });
}

export const timeRounder = (time: number): number => {
  const hours = time / 3600;

  // const doubledHours = hours * 2;
  // const rounded = Math.round(doubledHours);
  // const roundedHours = rounded / 2;

  // return Number(roundedHours.toFixed(2));

  return Number(hours.toFixed(2));
};

export const getShiftTime = (
  breakdown: BreakDown[],
  shift:
    | "Weekend"
    | "Overnight"
    | "Normal"
    | "Normal Hours"
    | "Overnight Hours",
): string => {
  const breaks = Object.values(breakdown).find((down) => down.name === shift);
  const breakTime =
    breaks?.time === 0 ? "-" : secToDateFormat(breaks?.time ?? 0);

  return breakTime;
};
export const getShiftTimeFloat = (
  breakdown: BreakDown[],
  shift:
    | "Weekend"
    | "Overnight"
    | "Normal"
    | "Normal Hours"
    | "Overnight Hours",
): string => {
  const breaks = Object.values(breakdown).find((down) => down.name === shift);
  const breakTime =
    breaks?.time === 0 ? "0" : secToFloatHours(breaks?.time ?? 0);

  return breakTime.toString();
};

export const dateFormat = (
  date: string | Date | undefined | null,
  Dateformat?: string,
): string => {
  try {
    if (!date) {
      return "-";
    }
    let timezone: string | null | undefined;

    if (isCustomerDomain) {
      const customerData = typedLocalStorage.getItem(
        APP_LOCAL_KEYS.CUSTOMER_DATA,
      ) as CustomerVerified | null;
      timezone = customerData?.customer?.workspaces?.timezone;
    } else {
      const workspaceDetails = typedLocalStorage.getItem(
        APP_LOCAL_KEYS.WORKSPACE_DATA,
      ) as Workspace | null;
      timezone =
        workspaceDetails?.timezone ||
        States.find((state) => state?.name === workspaceDetails?.state)
          ?.timezone;
    }

    return formatInTimeZone(
      date,
      timezone ?? APP_CONFIG.DEFAULT_TIMEZONE,
      Dateformat || "dd/MM/yyyy",
    );
  } catch (error) {
    return "-";
  }
};

//Function to convert minutes to hours
export const dataToHours = (data: number): string => {
  return (data / 60).toFixed(0);
};

/**
 * Combines a date with the current time
 * @param {Date} dateOnly - A date object (typically from a date picker)
 * @returns {Date} A new date object with the input date combined with the current time
 */
export const combineDateWithCurrentTime = (
  dateOnly: string | Date,
): Date | null => {
  if (!dateOnly) {
    return null;
  }
  // Convert string to Date object if it's not already
  const dateObj = typeof dateOnly === "string" ? new Date(dateOnly) : dateOnly;

  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return null;
  }

  const now = new Date();

  return new Date(
    dateObj.getFullYear(),
    dateObj.getMonth(),
    dateObj.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
    now.getMilliseconds(),
  );
};

/**
 * Get the user's timezone from workspace settings using Redux selector
 * @returns {string} The user's timezone or fallback timezone
 */
export const getUserTimezone = (): string => {
  try {
    const timezone = selectWorkspaceTimeZone(store.getState());

    return timezone || "America/New_York"; // fallback timezone
  } catch (error) {
    return "America/New_York"; // fallback timezone
  }
};

/**
 * Converts a date to a UTC-midnight ISO string using the workspace timezone.
 * When no date is provided, the current date is used.
 * This replaces DateStringToZeroDate for timezone-aware workflows.
 *
 * @param {Date} [date] - Date to convert (defaults to now).
 * @returns {string} UTC midnight ISO string, e.g. "2026-06-17T00:00:00.000Z".
 */
export const toWorkspaceZeroDate = (date: Date = new Date()): string => {
  const timezone = getUserTimezone();

  return formatInTimeZone(date, timezone, "yyyy-MM-dd") + "T00:00:00.000Z";
};

/**
 * Converts a UTC ISO date to a local-midnight Date using the workspace timezone.
 * Use this to set form defaults for DateInput so the calendar displays
 * the correct date regardless of the browser's timezone.
 *
 * @param {Date | string} date - UTC date (ISO string or Date object).
 * @returns {Date} Local-midnight Date representing the date in workspace timezone.
 */
export const toWorkspaceLocalDate = (date: Date | string): Date => {
  const dateString = typeof date === "string" ? date : date.toISOString();

  if (!dateString.includes("T")) {
    return new Date(dateString + "T00:00:00");
  }

  const isUtcMidnight =
    dateString.endsWith("T00:00:00.000Z") || dateString.endsWith("T00:00:00Z");
  const utcMidnight = isUtcMidnight
    ? dateString
    : toWorkspaceZeroDate(new Date(date));

  return new Date(utcMidnight.split("T")[0] + "T00:00:00");
};

/**
 * Convert UTC time to local timezone for display in TimePicker
 * @param {Date | string} utcDate - UTC date/time from backend
 * @returns {Date} Date object adjusted to local timezone
 */
export const utcToLocalTimezone = (utcDate: Date | string): Date => {
  if (!utcDate) {
    return new Date();
  }

  const timezone = getUserTimezone();
  const date = typeof utcDate === "string" ? new Date(utcDate) : utcDate;

  return toZonedTime(date, timezone);
};

/**
 * Convert local timezone time back to UTC for submission to backend
 * @param {Date} localDate - Date object in local timezone
 * @returns {Date} Date object in UTC
 */
export const localTimezoneToUtc = (localDate: Date): Date => {
  if (!localDate) {
    return new Date();
  }

  const timezone = getUserTimezone();

  return fromZonedTime(localDate, timezone);
};

/**
 * Convert time to seconds considering workspace timezone
 * @param {string} isoTimestamp - ISO timestamp string
 * @returns {number} - Time in seconds since midnight in workspace timezone
 */
export const timeToSecondsWithTimezone = (isoTimestamp: string): number => {
  const date = new Date(isoTimestamp);
  const timezone = getUserTimezone();

  // Format the time in the workspace timezone to extract hours and minutes
  const hours = parseInt(formatInTimeZone(date, timezone, "H"), 10);
  const minutes = parseInt(formatInTimeZone(date, timezone, "m"), 10);

  return hours * 3600 + minutes * 60;
};

const DATE_FORMAT = "MMM d, yyyy";
export const DATE_TIME_FORMAT = "MMM d, yyyy, h:mm a"; //Apr 28, 2025, 12:00 AM
export const DAY_MONTH_YEAR_TIME_FORMAT = "dd MMM, yyyy h:mm a"; //03 Mar, 2026 03:16 PM
export const DATE_TIME_DIGIT_FORMAT = "dd/MM/yyyy, h:mm a"; //28/04/2025, 12:00 AM
export const TIME_ONLY_FORMAT = "h:mm a"; //12:00 AM
export const DAY_MONTH_YEAR_FORMAT = "dd MMM, yyyy"; //28 Apr, 2025
export const DAY_MONTH_YEAR_FORMAT_SHORT = "dd MMM yyyy"; //28 Apr 2025
export const DAY_MONTH_YEAR_FORMAT_SHORTER = "d MMM yyyy"; //3 Apr 2025
export const MONTH_DAY_YEAR_FORMAT = "MMM dd, yyyy"; //Apr 28, 2025
export const DAY_MONTH_YEAR_FORMAT_WITH_DAY = "eee, dd MMM"; //Mon, 28 Apr
export const DAY_MONTH_YEAR_FORMAT_WITH_DAY_AND_YEAR = "eee, dd MMM yyyy"; //Mon, 28 Apr 2025
export const YEAR_MONTH_DAY_FORMAT = "yyyy/MM/dd"; //2025/04/28
export const DAY_DATE_TIME_FORMAT_WITH_DAY = "eee, MMM do yyyy"; // Mon, Apr 28, 2025
export const YEAR_MONTH_DAY_FORMAT2 = "yyyy-MM-dd"; //2025-04-28
export const DAY_MONTH_YEAR = "do MMMM yyyy"; //28th April 2025
export const DAY_MONTH_YEAR_FORMAT2 = "dd/MM/yyyy"; //28/04/2025
export const MONTH_YEAR_FORMAT = "MMMM yyyy"; //April 2025
export const LLL_DD_YEAR_FORMAT = "LLL dd, yyyy"; //April 28, 2025
export const MONTH_DAY_YEAR_FORMAT_WITH_DAY = "MMMM do, yyyy"; //April 28th, 2025

/**
 * Returns a human-readable relative time string from the given date to now.
 * @param {string} dateString - The date string to be converted into a relative time.
 * @param {number} justNowThresholdSecond - The threshold in seconds to determine when to display 'Just now'.
 * @param {boolean} includeDays - Whether to include day-level granularity in the output (e.g., '2 days ago').
 * @returns {string} A string representing the relative time from now.
 */
export function getRelativeTimeFromNow(
  dateString: string,
  justNowThresholdSecond = 30,
  includeDays = false,
): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < justNowThresholdSecond) {
    return "Just now";
  }
  if (seconds < 60) {
    return `${seconds} seconds ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hr${hours > 1 ? "s" : ""} ago`;
  }

  if (includeDays) {
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days}d ago`;
    }

    const weeks = Math.floor(days / 7);
    if (weeks < 5) {
      return `${weeks}w  ago`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months}mon  ago`;
    }

    const years = Math.floor(days / 365);
    return `${years}yr ago`;
  }

  return `${format(date, DATE_FORMAT)} at ${format(date, TIME_ONLY_FORMAT)}`;
}
/**
 * Converts decimal hours to human readable duration
 * @param {number} value - decimal hours (e.g. 1.5 for 1 hour and 30 minutes)
 * @returns {string} formatted string in hours and minutes
 */
export const hourToString = (value: number): string => {
  if (isNaN(value) || value === null || value === undefined) {
    return "0 min";
  }

  const totalMinutes = Math.round(value * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (hours > 0) {
    parts.push(`${hours} hr${hours !== 1 ? "s" : ""}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} min${minutes !== 1 ? "s" : ""}`);
  }

  return parts.join(" ") || "0 min";
};

export const todayYesterDay = (
  date: string,
  format: string = YEAR_MONTH_DAY_FORMAT,
): string => {
  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime())) {
    return "-";
  }

  const dateDayFormatted = dateFormat(parsedDate, YEAR_MONTH_DAY_FORMAT);

  if (dateDayFormatted === "-") {
    return "-";
  }

  const todayDayFormatted = dateFormat(new Date(), YEAR_MONTH_DAY_FORMAT);

  if (dateDayFormatted === todayDayFormatted) {
    return "Today";
  }

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayDayFormatted = dateFormat(yesterday, YEAR_MONTH_DAY_FORMAT);

  if (dateDayFormatted === yesterdayDayFormatted) {
    return "Yesterday";
  }

  return format === YEAR_MONTH_DAY_FORMAT
    ? dateDayFormatted
    : dateFormat(parsedDate, format);
};

/**
 * Calculates the total working hours given start time, end time, and break duration (all in seconds).
 * Handles overnight shifts where end time is less than start time (i.e., shift passes midnight).
 * @param {number} startTimeSec - The start time in seconds since midnight (0-86399).
 * @param {number} endTimeSec - The end time in seconds since midnight (0-86399).
 * @param {number} breakSec - The break duration in seconds.
 * @param {number} travelSec - The travel duration in seconds.
 * @returns {number} The total working hours in seconds.
 * @example
 * // Day shift, 8:00 to 17:00 with 1 hour break
 * calcTotalWorkingHours(28800, 61200, 3600) // returns 32400
 */
export function calcTotalWorkingHours(
  startTimeSec: number,
  endTimeSec: number,
  breakSec: number = 0,
  travelSec: number = 0,
): number {
  let totalSec;
  if (startTimeSec < endTimeSec) {
    totalSec = endTimeSec - startTimeSec;
  } else if (startTimeSec === endTimeSec) {
    totalSec = 86400;
  } else {
    // Overnight shift: e.g., 22:00 to 6:00
    totalSec = 86400 - startTimeSec + endTimeSec;
  }
  const workingSec = Math.max(0, totalSec - breakSec + travelSec);

  return workingSec;
}

/**
 * Parses a query string with startDate and endDate into a formatted fortnight range text.
 * Example input: "?startDate=2025-07-20T18%3A15%3A00.000Z&endDate=2025-08-03T18%3A14%3A59.000Z"
 * Output: "20 Jul 2025 - 03 Aug 2025"
 * @param {string} query - The query string to parse
 * @param {string} dateFormat - The format to use for the date (defaults to DAY_MONTH_YEAR_FORMAT)
 * @returns {string} The formatted fortnight range text
 */
export function parseFortnightRangeText(
  query: string,
  dateFormat: string = DAY_MONTH_YEAR_FORMAT,
): string {
  // Remove leading '?' if present
  const cleanQuery = query.startsWith("?") ? query.slice(1) : query;
  const params = new URLSearchParams(cleanQuery);

  const startDateStr = params.get("startDate");
  const endDateStr = params.get("endDate");

  if (!startDateStr || !endDateStr) {
    return "-";
  }

  const startDate = new Date(decodeURIComponent(startDateStr));
  const endDate = new Date(decodeURIComponent(endDateStr));

  return format(startDate, dateFormat) + " - " + format(endDate, dateFormat);
}

/**
 * Formats clock time based on isTwelve flag and time value
 * @param {string | number | Date | undefined} time - The time to format
 * @param {boolean} isTwelve - Whether the time should be treated as a 12-hour format
 * @param {boolean} isRoster - Whether this is roster time data
 * @returns {string} Formatted time string or "-- : --" if time is undefined/falsy and isTwelve is false
 */
export const formatClockTimeWithTwelve = (
  time: string | number | Date | undefined | null,
  isTwelve: boolean = false,
  isRoster: boolean = false,
): string => {
  if (time === undefined || time === null || (!isTwelve && !time)) {
    return "-- : --";
  }

  let parsedDate: Date | null = null;

  if (typeof time === "number") {
    parsedDate = secToDate(time);
  } else if (typeof time === "string" || time instanceof Date) {
    parsedDate = new Date(time);
  }

  if (!parsedDate || isNaN(parsedDate.getTime())) {
    return "-- : --";
  }

  if (isRoster) {
    return dateFormat(parsedDate, TIME_ONLY_FORMAT);
  }

  return parsedDate.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Resets the work breakdown array with default values for Normal Hours, Overnight Hours, and Weekend
 * @param {Array} current - The current work breakdown array
 * @param {TimeShift[]} shifts - The available shifts
 * @returns {Array} - Array of breakdown objects with reset values
 */
export const resetBreakdown = (
  current: {
    id?: string;
    shiftId: string;
    title?: string;
    time: number;
  }[],
  shifts: TimeShift[],
): {
  id: string;
  shiftId: string;
  title: string;
  time: number;
}[] => {
  const shiftTitles = ["Normal Hours", "Overnight Hours", "Weekend"];

  return shiftTitles.map((title, idx) => {
    const shift = shifts?.find((s: TimeShift) => s.title === title);

    return {
      id: current[idx]?.id || "",
      shiftId: shift?.id || "",
      title,
      time: 0,
    };
  });
};

/**
 * Create default time that shows as 12:00 AM in local timezone
 * @returns {Date} The default start time in UTC
 */
export const getDefaultStartTime = (): Date => {
  const localMidnight = new Date(new Date().setHours(0, 0, 0, 0));

  return localTimezoneToUtc(localMidnight);
};

/**
 * Checks if the given date is today in the user's timezone
 * @param {Date} date - The date to check
 * @returns {boolean} True if the date is today, false otherwise
 */
export const currentDayIsToday = (date: Date): boolean => {
  if (!date || isNaN(date.getTime())) {
    return false;
  }

  const timezone = getUserTimezone();
  const todayFormatted = formatInTimeZone(new Date(), timezone, "yyyy-MM-dd");
  const dateFormatted = formatInTimeZone(date, timezone, "yyyy-MM-dd");

  return todayFormatted === dateFormatted;
};

/**
 * @description it returns the date in yyyy-MM-dd format (ISO date part)
 * @param {Date} date - date to format
 * @returns {string} - formatted date
 */
export const toISODateString = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

/**
 * Get the next day ISO string from an ISO date string
 * @param {string} isoDate - The input ISO date string
 * @returns {string} The next day ISO date string
 */
export const getNextDayDateISO = (isoDate: string): string => {
  const next = new Date(isoDate);
  next.setDate(next.getDate() + ONE);

  return next.toISOString();
};

/**
 * Initialize a time with default values
 * @param {string | number | Date} time - The time to initialize
 * @param {boolean} isTwelve - Whether the time should be treated as a 12-hour format
 * @param {string | number | Date} fallback - The fallback time if the time is undefined
 * @returns {Date | undefined} The initialized time
 */
export const initTime = (
  time?: string | number | Date,
  isTwelve?: boolean,
  fallback?: string | number | Date,
): Date | undefined => {
  if (time === 0 && isTwelve === false) {
    return undefined;
  }

  return new Date(fallback ?? new Date().setHours(0, 0, 0, 0));
};

/**
 * Check if a given date exists within an optional date range
 * @param {TDateRangeCheckArgs} params - The start date of the range
 * @returns {boolean} Whether the date exists within the range
 */
export const dateExistsInRange = (params: DateRangeCheckParams): boolean => {
  const { date, startDate, endDate } = params;
  if (!date) {
    return true;
  }

  const target = new Date(date).getTime();
  if (isNaN(target)) {
    return true;
  }

  const start = startDate != null ? new Date(startDate).getTime() : -Infinity;

  const end = endDate != null ? new Date(endDate).getTime() : Infinity;

  return target >= start && target <= end;
};

export const minDate = (a: Date, b: Date): Date =>
  new Date(Math.min(a.getTime(), b.getTime()));

export const toValidDate = (
  d: string | number | Date | null | undefined,
): Date | undefined => {
  if (d == null) {
    return undefined;
  }

  let date: Date;

  if (d instanceof Date) {
    date = d;
  } else if (typeof d === "string" || typeof d === "number") {
    date = new Date(d);
  } else {
    return undefined;
  }

  return isNaN(date.getTime()) ? undefined : date;
};
